<template>
  <div class="detail">
    <div class="detailTop">
      <!-- Touches timeline -->
      <div class="touches">
        <div class="sectionLabel">{{ touchesLabel }}</div>
        <!-- Touches are fetched on expand, so the list is genuinely empty for a moment.
             Say so in the list itself: the section label alone is easy to miss, and a bare
             gap reads as "this call has no touches". -->
        <p v-if="touchesLoading" class="touchesState">Loading calls…</p>
        <p v-else-if="touchesErr" class="touchesState">{{ touchesErr }}</p>
        <button
          v-for="(t, i) in touches"
          :key="i"
          type="button"
          class="touch"
          :class="{ sel: i === selectedIdx }"
          @click="selectTouch(i)"
        >
          <span class="tNum">{{ i + 1 }}</span>
          <DispositionPill class="tPill" :code="t.outcome || failureDisplayCode(t.error_class)" />
          <!-- Which line this touch rang — the number lives on the touch now, not the row. -->
          <span v-if="t.prospect_e164" class="tLineNum">{{ formatPhone(t.prospect_e164) }}</span>
          <span class="tWhen">{{ formatAttemptStamp(t.at) }}</span>
          <span class="tRight">
            <span v-if="formatDuration(t.duration_seconds)" class="tDur">{{ formatDuration(t.duration_seconds) }}</span>
            <!-- Exported → read-only "Synced" deep link to whichever CRM holds the id (hover-only). -->
            <span
              v-if="isSynced(t)"
              class="aff synced"
              :class="{ link: !!syncedUrl(t) }"
              role="link"
              :title="syncedTitle(t)"
              @click.stop="openSynced(t)"
            >Synced <FavIcon :domain="syncedDomain(t)" :size="13" /></span>
          </span>
        </button>
      </div>

      <!-- Summary / Transcript tabs for the selected touch -->
      <div class="convo">
        <div class="tabs">
          <button type="button" class="tab" :class="{ on: tab === 'summary' }" @click="tab = 'summary'">
            <AppIcon name="story" :size="13" />Summary
          </button>
          <button type="button" class="tab" :class="{ on: tab === 'transcript' }" @click="tab = 'transcript'">
            <AppIcon name="chat_history" :size="13" />Transcript
          </button>
        </div>

        <div class="tabBody">
          <!-- Blank until the touches land. There's no selected touch yet, so the "no
               summary" copy would assert an absence we haven't established, and repeating
               the list's loading message here would just be noise. This has to guard the
               whole chain: gating only the first branch would let `tab === 'summary'` win
               while loading and dereference a null selRecording. -->
          <template v-if="!touchesLoading && !touchesErr">
            <p v-if="!selRecording" class="muted">
              A summary appears once a call connects or a voicemail is left.
            </p>

            <template v-else-if="tab === 'summary'">
              <template v-if="selRecording.summary">
                <p class="summaryText">{{ selRecording.summary }}</p>
                <p class="autogen">Auto-generated</p>
              </template>
              <p v-else class="muted">{{ summaryEmptyMsg }}</p>
            </template>

            <template v-else>
              <p v-if="transcriptLoading" class="muted">Loading transcript…</p>
              <p v-else-if="txErr" class="muted">{{ txErr }}</p>
              <div v-else-if="utterances.length" class="utterances">
                <div v-for="(u, ui) in utterances" :key="ui" class="utt" :class="u.role === 'rep' ? 'rep' : 'prospect'">
                  <span class="speaker">{{ speakerLabel(u.role) }}</span>
                  <span class="uttText">{{ u.transcript }}</span>
                  <span class="uttTime">{{ formatClock(u.start) }}</span>
                </div>
              </div>
              <p v-else class="muted">{{ transcriptEmptyMsg }}</p>
            </template>
          </template>
        </div>
      </div>
    </div>

    <!-- Notes are written during the call (and exported with the CRM activity); history reads them. -->
    <div class="notes">
      <div class="sectionLabel">
        {{ lineNotes.length > 1 ? 'Notes from recent calls' : 'Notes from most recent call' }}
      </div>
      <!-- Several lines carry notes → show each with the line it was typed on. -->
      <template v-if="lineNotes.length > 1">
        <div v-for="n in lineNotes" :key="n.prospect_e164" class="noteBlock">
          <div class="noteLineLabel">{{ formatPhone(n.prospect_e164) }}</div>
          <p class="notesBody">{{ n.note?.text }}</p>
          <div v-if="noteMetaFor(n.note)" class="notesMeta">{{ noteMetaFor(n.note) }}</div>
        </div>
      </template>
      <template v-else>
        <p v-if="noteText" class="notesBody">{{ noteText }}</p>
        <p v-else class="notesBody empty">{{ noNotesCopy }}</p>
        <div v-if="noteMeta" class="notesMeta">{{ noteMeta }}</div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import AppIcon from '../../../components/AppIcon.vue'
import FavIcon from '../../../components/FavIcon.vue'
import DispositionPill from './DispositionPill.vue'
import { failureDisplayCode } from './dialerDispositions'
import { formatAttemptStamp, formatDuration, formatClock, relativeAgo } from '../../utils/localTime'
import { formatPhone } from '../../utils/phone'
import {
  getDialerContactTimeline,
  getDialerEntryTimeline,
  getRecordingTranscript,
  type DialerHistoryItem,
  type DialerHistoryNote,
  type DialerHistoryTimelineEntry,
  type DialerTranscriptResponse,
} from '../../../services/dialerApi'
import { hubspotCallReviewUrl, salesforceTaskReviewUrl } from '../../../services/crmLinks'

const props = defineProps<{ item: DialerHistoryItem }>()

// Touches are fetched here rather than inlined on the row.
const touches = ref<DialerHistoryTimelineEntry[]>([])
const touchesLoading = ref(false)
const touchesErr = ref('')

// While the touches are pending the count isn't known, so the header stays neutral and the
// list body below carries the state — one loading message, where the rows will appear.
const touchesLabel = computed(() => {
  if (touchesLoading.value || touchesErr.value) return 'Calls'
  const n = touches.value.length
  return `${n} ${n === 1 ? 'touch' : 'touches'} — click a conversation for its summary`
})

// ── Read-only "Synced" deep-link per touch — HubSpot or Salesforce, by whichever id is present ─────────
function isSynced(t: DialerHistoryTimelineEntry): boolean {
  return !!(t.hubspot_engagement_id || t.salesforce_task_id)
}

function syncedUrl(t: DialerHistoryTimelineEntry): string | null {
  if (t.hubspot_engagement_id) return hubspotCallReviewUrl(t.hubspot_engagement_id)
  if (t.salesforce_task_id) return salesforceTaskReviewUrl(t.salesforce_task_id)
  return null
}

function syncedLabel(t: DialerHistoryTimelineEntry): string {
  return t.hubspot_engagement_id ? 'HubSpot' : 'Salesforce'
}

function syncedDomain(t: DialerHistoryTimelineEntry): string {
  return t.hubspot_engagement_id ? 'hubspot.com' : 'salesforce.com'
}

function syncedTitle(t: DialerHistoryTimelineEntry): string {
  return syncedUrl(t) ? `Open this call in ${syncedLabel(t)}` : `Synced to ${syncedLabel(t)}`
}

function openSynced(t: DialerHistoryTimelineEntry) {
  const url = syncedUrl(t)
  if (url) window.open(url, '_blank', 'noopener')
}

// Default to the last touch that has a recording (its summary/transcript is the most
// useful), else the last touch.
function defaultIdx(): number {
  const ts = touches.value
  for (let i = ts.length - 1; i >= 0; i -= 1) if (ts[i].recording) return i
  return Math.max(0, ts.length - 1)
}
const selectedIdx = ref(0)
const tab = ref<'summary' | 'transcript'>('summary')

const selRecording = computed(() => touches.value[selectedIdx.value]?.recording || null)

function selectTouch(i: number) {
  selectedIdx.value = i
}

async function loadTouches() {
  touchesErr.value = ''
  const contactId = props.item.contact_id
  const entryId = props.item.entry_id
  if (!contactId && !entryId) {
    touches.value = []
    return
  }
  touchesLoading.value = true
  try {
    // A linked person's timeline spans ALL their lines; an unlinked number (no contact)
    // keeps the entry-keyed, number-scoped fetch.
    touches.value = contactId
      ? await getDialerContactTimeline(contactId)
      : await getDialerEntryTimeline(entryId as string)
    selectedIdx.value = defaultIdx()
  } catch {
    // Don't fall back to an empty list silently — that renders identically to "this row
    // has no calls", which for a history row is never true.
    touches.value = []
    touchesErr.value = 'Could not load the calls.'
  } finally {
    touchesLoading.value = false
  }
}

// `immediate` covers the mount (this component only exists while a row is expanded); the
// watcher covers the parent reusing the same slot for a different person after a filter
// change. Declared after selectedIdx/defaultIdx so the immediate run isn't in their TDZ.
watch(() => props.item.contact_id || props.item.entry_id, loadTouches, { immediate: true })

// ── Transcript (lazy-loaded per recording, cached) ──────────────────────────
const transcripts = ref<Record<string, DialerTranscriptResponse>>({})
const txLoadingId = ref<string | null>(null)
const txErr = ref('')

async function ensureTranscript() {
  const rec = selRecording.value
  if (!rec || transcripts.value[rec.id] || txLoadingId.value === rec.id) return
  txLoadingId.value = rec.id
  txErr.value = ''
  try {
    transcripts.value = { ...transcripts.value, [rec.id]: await getRecordingTranscript(rec.id) }
  } catch {
    txErr.value = 'Could not load the transcript.'
  } finally {
    txLoadingId.value = null
  }
}

watch([tab, selectedIdx], () => {
  if (tab.value === 'transcript') void ensureTranscript()
})

const currentTx = computed(() => {
  const rec = selRecording.value
  return rec ? transcripts.value[rec.id] || null : null
})
const utterances = computed(() => currentTx.value?.utterances || [])
const transcriptLoading = computed(
  () => !!selRecording.value && txLoadingId.value === selRecording.value.id,
)
const transcriptEmptyMsg = computed(() => {
  const status = currentTx.value?.transcript_status || selRecording.value?.transcript_status
  if (status === 'pending') return 'Transcript is still being generated.'
  if (status === 'failed') return 'Transcription failed for this call.'
  if (status === 'skipped') return 'No transcript (transcription is off for this workspace).'
  return 'No transcript available for this call.'
})
const summaryEmptyMsg = computed(() => {
  const status = selRecording.value?.transcript_status
  if (status === 'pending') return 'Summary is still being generated.'
  if (status === 'failed') return 'Summary could not be generated for this call.'
  return 'No summary available for this call.'
})

const prospectFirst = computed(
  () => (props.item.name || '').trim().split(/\s+/)[0]?.toUpperCase() || 'PROSPECT',
)
function speakerLabel(role: string): string {
  return role === 'rep' ? 'REP' : prospectFirst.value
}

// ── Notes taken on this person's calls (read-only here) ──────────────────────
// One note → the plain block; notes on several lines → each with its line label.
const lineNotes = computed(() => (props.item.numbers || []).filter((n) => n.note?.text))
const noteText = computed(() => props.item.note?.text || '')

function noteMetaFor(note: DialerHistoryNote | null | undefined): string {
  if (!note) return ''
  // Notes typed on the queue page record no author, and the row may be a teammate's.
  return [note.author_name, relativeAgo(note.at)].filter(Boolean).join(' · ')
}
const noteMeta = computed(() => noteMetaFor(props.item.note))

const noNotesCopy = computed(() =>
  props.item.contact_id
    ? 'No notes were taken for this contact.'
    : 'No notes were taken on this number.',
)

// Re-seed local state if the same component instance is reused for another row.
watch(
  () => props.item.contact_id || props.item.prospect_e164,
  () => {
    selectedIdx.value = defaultIdx()
    tab.value = 'summary'
    txErr.value = ''
  },
)
</script>

<style scoped>
.detail {
  padding: 8px 12px 16px 40px;
  background: var(--color-bg-subtle, #f2ede5);
  border-top: 1px solid var(--color-border, #e5e2dc);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detailTop {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.3fr);
  gap: 28px;
}

.sectionLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary, #a3a3a3);
  margin-bottom: 8px;
}

/* Touches */
.touches {
  min-width: 0;
}
/* Stands in for the touch rows while they load (or if they fail), so the list reads as
   pending rather than empty. Padded to roughly one row's height to limit the reflow when
   the real touches arrive. */
.touchesState {
  font-size: 13px;
  color: var(--color-text-tertiary, #a3a3a3);
  margin: 0;
  padding: 9px 0;
}
.touch {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  border-radius: var(--radius-md, 8px);
  padding: 6px 8px;
  cursor: pointer;
  font: inherit;
  color: inherit;
}
.touch:hover {
  background: var(--color-bg-surface, #fff);
}
.touch.sel {
  background: var(--color-bg-surface, #fff);
  box-shadow: inset 0 0 0 1px var(--color-border, #e5e2dc);
}
.tNum {
  width: 18px;
  height: 18px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--color-bg-app, #ece7df);
  color: var(--color-text-secondary, #525252);
  font-size: 11px;
  font-weight: 700;
}
.tPill {
  flex: none;
}
/* The line this touch rang. Shrinks (with ellipsis) before the timestamp does. */
.tLineNum {
  font-size: 12px;
  color: var(--color-text-secondary, #525252);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 0 1 auto;
}
.tWhen {
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
  white-space: nowrap;
}
.tDur {
  font-size: 12px;
  color: var(--color-text-secondary, #525252);
  font-variant-numeric: tabular-nums;
}
/* Right-aligned group: duration + the hover-only HubSpot affordance, kept together. */
.tRight {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
/* Synced affordance — revealed only while the touch row is hovered (independent of selection). */
.aff {
  display: none;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.touch:hover .aff {
  display: inline-flex;
}
.aff.synced {
  color: var(--color-text-secondary, #525252);
}
.aff.synced.link {
  cursor: pointer;
}
.aff.synced.link:hover {
  color: var(--color-text-primary, var(--text));
  text-decoration: underline;
}

/* Conversation panel */
.convo {
  min-width: 0;
  border-left: 1px solid var(--color-border, #e5e2dc);
  padding-left: 24px;
}
.tabs {
  display: flex;
  gap: 16px;
  border-bottom: 1px solid var(--color-border, #e5e2dc);
  margin-bottom: 12px;
}
.tab {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 0;
  background: transparent;
  padding: 0 0 8px;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-tertiary, #a3a3a3);
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
}
.tab.on {
  color: var(--color-text-primary, var(--text));
  border-bottom-color: var(--color-accent, #0093da);
}

.tabBody {
  min-height: 60px;
}
.summaryText {
  font-size: 13px;
  line-height: 1.55;
  color: var(--color-text-primary, var(--text));
  margin: 0;
}
.autogen {
  margin: 8px 0 0;
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
}
.muted {
  font-size: 13px;
  color: var(--color-text-tertiary, #a3a3a3);
  margin: 0;
}

.utterances {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}
.utt {
  display: grid;
  grid-template-columns: 56px 1fr auto;
  gap: 10px;
  align-items: start;
  font-size: 13px;
  line-height: 1.45;
}
.speaker {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary, #a3a3a3);
  padding-top: 2px;
}
.utt.rep .uttText {
  color: var(--color-text-primary, var(--text));
}
.utt.prospect .uttText {
  color: var(--color-text-secondary, #525252);
}
.uttTime {
  font-size: 11px;
  color: var(--color-text-tertiary, #a3a3a3);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Notes */
.notes {
  max-width: 760px;
}
.noteBlock + .noteBlock {
  margin-top: 12px;
}
.noteLineLabel {
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
  font-variant-numeric: tabular-nums;
  margin-bottom: 4px;
}
.notesBody {
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.55;
  white-space: pre-wrap;
  color: var(--color-text-primary, var(--text));
  background: var(--color-bg-surface, #fff);
  border: 1px solid var(--color-border, #e5e2dc);
  border-radius: var(--radius-md, 8px);
}
.notesBody.empty {
  color: var(--color-text-tertiary, #a3a3a3);
  background: transparent;
  border-style: dashed;
}
.notesMeta {
  margin-top: 6px;
  font-size: 12px;
  color: var(--color-text-tertiary, #a3a3a3);
}
</style>
